<template>
  <div class="page-admin-field-mappings">
    <div class="page-admin-field-mappings__content">
      <!-- Phones: its main action, as the "+" of the settings header, whose back leads to the profiles. -->
      <PageMainAction :label="$t('page.admin-sync-profiles.add-mapping')" @click="onAddClick" />
      <div v-if="!isPhone" class="page-admin-field-mappings__back" @click="$router.push(paths.adminSyncProfiles)">
        <BaseIcon name="arrow-left" />
        {{ $t('page.admin-sync-profiles.back') }}
      </div>

      <div class="page-admin-field-mappings__profile-title" v-if="profile">
        {{ profile.name }}
      </div>

      <div class="page-admin__table-header">
        <div class="page-admin-field-mappings__header-left">
          <div v-if="!isPhone" class="page-admin__count">
            {{ filteredMappings.length }} {{ $t('page.admin-sync-profiles.mappings') }}
          </div>
          <BasePillGroup :options="scopeFilterOptions" :value="scopeFilter" @input="scopeFilter = $event" />
        </div>
        <div v-if="!isPhone" class="page-admin__actions">
          <BaseRefreshButton :size="40" :pending="pending" @refresh="refresh" />
          <BaseButton has-icon-left is-outlined @click.native="onPreviewClick">
            <BaseIcon name="eye" />
            {{ $t('page.admin-sync-profiles.payload-preview.button') }}
          </BaseButton>
          <BaseButton has-icon-left @click.native="onAddClick">
            <BaseIcon name="plus" />
            {{ $t('page.admin-sync-profiles.add-mapping') }}
          </BaseButton>
        </div>
      </div>

      <div class="page-admin-field-mappings__table-wrapper">
        <TableNormal
          :headers="headers"
          :items="filteredMappings"
          :items-per-page="-1"
          :loading="pending"
          :loading-text="$t('page.admin-sync-profiles.loading')"
          :no-data-text="$t('page.admin-sync-profiles.no-mappings')"
          hide-default-footer
          :sort-by="['displayOrder']"
          class="page-admin-field-mappings__table"
          @click:row="onEditClick"
        >
          <template v-slot:progress>
            <BaseLoadingBar v-if="pending" />
          </template>

          <!-- Phones: the table draws the count line above its rows (docs/page-layout.md). -->
          <template #phone-count>
            <span class="number-monospace">{{ filteredMappings.length }}</span>
            {{ ' ' + $t('page.admin-sync-profiles.mappings') }}
          </template>
          <template #phone-actions>
            <BaseRoundButton
              icon="eye"
              :size="36"
              is-white
              is-floating
              :aria-label="$t('page.admin-sync-profiles.payload-preview.button')"
              @click.native="onPreviewClick"
            />
            <BaseRefreshButton :pending="pending" @refresh="refresh" />
          </template>

          <template v-slot:item.crmFieldName="{ item }">
            <code class="page-admin-field-mappings__code" :title="item.crmFieldName">{{ item.crmFieldName }}</code>
          </template>

          <template v-slot:item.valueExpression="{ item }">
            <code class="page-admin-field-mappings__code" :title="item.valueExpression">
              <span
                v-for="(part, i) in expressionParts(item.valueExpression)"
                :key="i"
                :class="part.dynamic ? 'page-admin-field-mappings__expr-dynamic' : null"
                >{{ part.text }}</span
              >
            </code>
          </template>

          <template v-slot:item.label="{ item }">
            <span v-if="item.label">{{ item.label }}</span>
            <span v-else class="page-admin-field-mappings__null">{{ $t('basic-words.none') }}</span>
          </template>

          <template v-slot:item.fieldType="{ item }">
            <span>{{ $t(`page.admin-sync-profiles.field-types.${item.fieldType}`) }}</span>
          </template>

          <template v-slot:item.syncMode="{ item }">
            <span>{{ $t(`page.admin-sync-profiles.sync-modes.${syncModeKey(item.syncMode)}`) }}</span>
          </template>

          <template v-slot:item.entityScope="{ item }">
            <span>{{ item.entityScope ? item.entityScope.join(', ') : '—' }}</span>
          </template>

          <template v-slot:item.actions="{ item }">
            <button
              v-if="isPhone"
              type="button"
              class="base-phone-row__action"
              :aria-label="$t('actions.more-actions')"
              aria-haspopup="dialog"
              @click.stop="sheetMapping = item"
            >
              <BaseIcon name="more-horizontal" aria-hidden="true" />
            </button>
            <div v-else class="page-admin-field-mappings__actions-cell" @click.stop>
              <BaseRoundButton
                :size="36"
                icon="edit-2"
                is-white
                :tooltip-text="$t('actions.edit')"
                @click.native="onEditClick(item)"
              />
              <BaseRoundButton
                :size="36"
                icon="trash-2"
                is-white
                is-red
                :tooltip-text="$t('actions.delete')"
                @click.native="onDeleteClick(item)"
              />
            </div>
          </template>
        </TableNormal>
      </div>
    </div>

    <FieldMappingDialog
      ref="fieldMappingDialog"
      :profile-id="profileId"
      @action-succeeded="refresh"
      @delete-requested="onDeleteClick"
    />
    <BaseBottomSheet v-model="mappingSheetOpen" :title="sheetMapping ? sheetMapping.crmFieldName : ''">
      <ul v-if="sheetMapping" class="base-bottom-sheet__menu">
        <li>
          <button type="button" @click="fromSheet(onEditClick)">
            <BaseIcon name="edit-2" />
            {{ $t('actions.edit') }}
          </button>
        </li>
        <li>
          <button type="button" class="is-danger" @click="fromSheet(onDeleteClick)">
            <BaseIcon name="trash-2" />
            {{ $t('actions.delete') }}
          </button>
        </li>
      </ul>
    </BaseBottomSheet>
    <DeleteFieldMappingDialog ref="deleteFieldMappingDialog" :profile-id="profileId" @action-succeeded="refresh" />
    <PayloadPreviewDialog ref="payloadPreviewDialog" :profile-id="profileId" />
  </div>
</template>

<script>
import BaseBottomSheet from '@/components/base/BaseBottomSheet.vue';
import BaseButton from '@/components/base/BaseButton';
import BaseIcon from '@/components/base/BaseIcon';
import BaseLoadingBar from '@/components/base/BaseLoadingBar';
import BasePillGroup from '@/components/base/BasePillGroup';
import BaseRefreshButton from '@/components/base/BaseRefreshButton.vue';
import BaseRoundButton from '@/components/base/BaseRoundButton';
import DeleteFieldMappingDialog from '@/components/page-admin-sync-profiles/DeleteFieldMappingDialog';
import FieldMappingDialog from '@/components/page-admin-sync-profiles/FieldMappingDialog';
import PayloadPreviewDialog from '@/components/page-admin-sync-profiles/PayloadPreviewDialog';
import PageMainAction from '@/components/layout/PageMainAction.vue';
import TableNormal from '@/components/table/TableNormal.vue';
import { paths } from '@/util/paths';
import { viewport } from '@/util/viewport';
import { mapActions, mapGetters } from 'vuex';

export default {
  name: 'PageAdminSyncProfileFieldMappings',
  components: {
    TableNormal,
    BaseBottomSheet,
    BaseButton,
    BaseIcon,
    BaseLoadingBar,
    BasePillGroup,
    BaseRefreshButton,
    BaseRoundButton,
    DeleteFieldMappingDialog,
    PageMainAction,
    FieldMappingDialog,
    PayloadPreviewDialog,
  },
  data() {
    return {
      scopeFilter: 'all',
      // Phones: the field whose sheet is open.
      sheetMapping: null,
    };
  },
  computed: {
    ...mapGetters('adminSyncProfileFieldMappings', ['profile', 'fieldMappings', 'pending']),
    profileId() {
      return this.$route.params.profileId;
    },
    isPhone() {
      return viewport.size === 'phone';
    },
    mappingSheetOpen: {
      get() {
        return !!this.sheetMapping;
      },
      set(open) {
        if (!open) this.sheetMapping = null;
      },
    },
    paths() {
      return paths;
    },
    scopeFilterOptions() {
      return ['all', 'opportunity', 'quote'].map((scope) => ({
        value: scope,
        label: this.$t(`page.admin-sync-profiles.scope-filter.${scope}`),
      }));
    },
    filteredMappings() {
      const filtered =
        this.scopeFilter === 'all'
          ? this.fieldMappings
          : this.fieldMappings.filter((m) => m.entityScope && m.entityScope.includes(this.scopeFilter));
      // Primary sort: displayOrder. Secondary: crmFieldName (alphabetical, locale-aware).
      // Without the secondary key, rows that share the default 999 displayOrder appear in
      // whatever order the back-end returned, which jitters across reloads.
      return [...filtered].sort((a, b) => {
        const orderDiff = (a.displayOrder ?? 999) - (b.displayOrder ?? 999);
        if (orderDiff !== 0) return orderDiff;
        return (a.crmFieldName || '').localeCompare(b.crmFieldName || '');
      });
    },
    headers() {
      return [
        { text: this.$t('page.admin-sync-profiles.mapping-columns.crm-field'), value: 'crmFieldName', width: '200px' },
        { text: this.$t('page.admin-sync-profiles.mapping-columns.value-expression'), value: 'valueExpression' },
        {
          text: this.$t('page.admin-sync-profiles.mapping-columns.field-type'),
          value: 'fieldType',
          phone: 'fact',
          width: '100px',
        },
        {
          text: this.$t('page.admin-sync-profiles.mapping-columns.sync-mode'),
          value: 'syncMode',
          phone: 'fact',
          width: '120px',
        },
        {
          text: this.$t('page.admin-sync-profiles.mapping-columns.scope'),
          value: 'entityScope',
          phone: 'fact',
          width: '140px',
        },
        { text: this.$t('page.admin-sync-profiles.mapping-columns.order'), value: 'displayOrder', width: '80px' },
        { text: this.$t('page.admin-sync-profiles.mapping-columns.label'), value: 'label', phone: 'subtitle' },
        { text: this.$t('page.tenders.data-table-titles.actions'), value: 'actions', sortable: false, width: '100px' },
      ];
    },
  },
  methods: {
    ...mapActions('adminSyncProfileFieldMappings', ['loadProfile', 'getFieldMappings']),
    refresh() {
      this.getFieldMappings(this.profileId).catch(() =>
        this.$toast.error(this.$t('page.admin-sync-profiles.load-error')),
      );
    },
    onPreviewClick() {
      this.$refs.payloadPreviewDialog.openDialog();
    },
    onAddClick() {
      this.$refs.fieldMappingDialog.openDialog();
    },
    onEditClick(mapping) {
      this.$refs.fieldMappingDialog.openDialog(mapping);
    },
    syncModeKey(syncMode) {
      const map = { ALWAYS: 'always', CREATE_ONLY: 'create-only', UPDATE_ONLY: 'update-only' };
      return map[syncMode] ?? syncMode;
    },
    // Split a value-expression into alternating literal / dynamic-placeholder spans for
    // visual highlighting in the list. `[^}]*?` (zero-or-more, non-greedy) so empty
    // `{{}}` also counts as dynamic, and adjacent placeholders ({{a}}{{b}}) tokenize
    // correctly.
    expressionParts(expr) {
      if (!expr) return [];
      const re = /\{\{[^}]*?\}\}/g;
      const parts = [];
      let lastIndex = 0;
      let match;
      while ((match = re.exec(expr)) !== null) {
        if (match.index > lastIndex) {
          parts.push({ text: expr.slice(lastIndex, match.index), dynamic: false });
        }
        parts.push({ text: match[0], dynamic: true });
        lastIndex = match.index + match[0].length;
      }
      if (lastIndex < expr.length) {
        parts.push({ text: expr.slice(lastIndex), dynamic: false });
      }
      return parts;
    },
    // As in every sheet, a choice closes it first.
    fromSheet(action) {
      const mapping = this.sheetMapping;
      this.sheetMapping = null;
      action(mapping);
    },
    onDeleteClick(mapping) {
      this.$refs.fieldMappingDialog.closeDialog();
      this.$refs.deleteFieldMappingDialog.openDialog(mapping);
    },
  },
  async created() {
    await this.loadProfile(this.profileId).catch(() =>
      this.$toast.error(this.$t('page.admin-sync-profiles.load-error')),
    );
    this.refresh();
  },
};
</script>

<style lang="scss">
.page-admin-field-mappings {
  &__content {
    padding: 32px;
  }

  &__back {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    color: $color-normal-pale;
    cursor: pointer;
    margin-bottom: 16px;
    transition: color 150ms ease;

    .base-icon {
      width: 16px;
      height: 16px;
    }

    &:hover {
      color: $color-accent;
    }
  }

  &__profile-title {
    font-size: 22px;
    font-weight: 700;
    margin-bottom: 16px;
    color: $color-normal;
  }

  .page-admin__table-header {
    align-items: flex-start;
  }

  &__header-left {
    display: flex;
    flex-direction: column;
    gap: 6px;

    .page-admin__count {
      margin-bottom: 0;
    }
  }

  &__table-wrapper {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  &__table tbody tr {
    cursor: pointer;
  }

  &__null {
    color: $color-normal-pale;
    font-style: italic;
  }

  &__code {
    // Display as a code chip so the technical fields read distinctly from prose columns.
    // Matches the inline `<code>` style used in the syntax help drawer and dialogs.
    font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
    font-size: 12.5px;
    color: $color-normal;
    background: $color-neutral-pale;
    padding: 2px 6px;
    border-radius: 3px;
    // Wrap when the content is too long for the cell. `overflow-wrap: anywhere` prefers
    // natural word boundaries but falls back to mid-token breaks for identifiers with
    // no whitespace (so long underscored names still wrap rather than overflow).
    overflow-wrap: anywhere;
  }

  // Dynamic placeholders ({{...}}) inside a value-expression code chip — accent color +
  // medium weight so they pop out from surrounding literal text when users scan the list.
  &__expr-dynamic {
    color: $color-accent;
    font-weight: 500;
  }

  &__actions-cell {
    display: flex;
    justify-content: flex-end;
    gap: 4px;
  }

  .v-data-table.theme--light tbody td {
    color: $color-normal;
  }

  .v-data-table__progress {
    .base-loading-bar {
      position: absolute;
      top: 48px;
    }
  }
}
</style>
